import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Calculator, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';

const FORMULES = {
  'gite-seul': 'Gite seul',
  'gite-salle': 'Gite + Salle',
  'salle-seule': 'Salle seule',
  'appartement-1': 'Apt Terrasse',
  'appartement-2': 'Apt Panorama',
};

const SOURCES = ['Telephone', 'Email', 'Sur place', 'Site direct', 'Autre'];

const EMPTY = {
  accommodation_type: 'gite-seul', arrival_date: '', departure_date: '',
  name: '', email: '', phone: '',
  guests_lodging: '2', guests_reception: '', adults: '2', children: '0',
  event_type: '', message: '',
  amount: '', tourist_tax: '', deposit_amount: '',
  status: 'confirmed', payment_status: 'unpaid', source: 'Telephone',
};

export default function AdminNewReservation({ open, onOpenChange, api, onCreated }) {
  const [f, setF] = useState(EMPTY);
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [availability, setAvailability] = useState(null); // null | 'checking' | 'available' | 'conflict'
  const [conflictConfirm, setConflictConfirm] = useState(false); // 409 received: offer "create anyway"

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const isSalle = f.accommodation_type === 'salle-seule';
  const hasSalle = isSalle || f.accommodation_type === 'gite-salle';

  useEffect(() => {
    setConflictConfirm(false);
    if (!f.arrival_date || !f.departure_date || f.departure_date <= f.arrival_date) {
      setAvailability(null);
      return;
    }
    setAvailability('checking');
    const t = setTimeout(() => {
      api('get', `/api/availability/check?accommodation_type=${f.accommodation_type}&arrival_date=${f.arrival_date}&departure_date=${f.departure_date}`)
        .then((r) => setAvailability(r.data.available ? 'available' : 'conflict'))
        .catch(() => setAvailability(null));
    }, 400);
    return () => clearTimeout(t);
  }, [f.accommodation_type, f.arrival_date, f.departure_date, api]);

  const calculate = async () => {
    if (!f.arrival_date || !f.departure_date) {
      toast.error('Renseignez les dates avant de calculer le prix');
      return;
    }
    setCalculating(true);
    try {
      const res = await api('post', '/api/simulate-price', {
        accommodation_type: f.accommodation_type,
        arrival_date: f.arrival_date,
        departure_date: f.departure_date,
        guests_lodging: parseInt(f.guests_lodging) || 1,
        guests_reception: f.guests_reception ? parseInt(f.guests_reception) : null,
        adults: parseInt(f.adults) || null,
        children: parseInt(f.children) || 0,
        event_type: f.event_type || null,
      });
      const p = res.data;
      setF((prev) => ({
        ...prev,
        amount: String(p.total ?? ''),
        tourist_tax: String(p.tourist_tax ?? '0'),
        deposit_amount: String(p.deposit ?? ''),
      }));
      toast.success(`Prix calcule : ${p.total} € (modifiable)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossible de calculer le prix pour ces criteres');
    } finally {
      setCalculating(false);
    }
  };

  const submit = async (force = false) => {
    if (!f.name || !f.arrival_date || !f.departure_date) {
      toast.error('Nom et dates sont obligatoires');
      return;
    }
    if (!isSalle && (parseInt(f.adults) || 0) < 1) {
      toast.error('Au moins 1 adulte est requis (taxe de sejour)');
      return;
    }
    setSaving(true);
    try {
      const res = await api('post', '/api/admin/reservations', {
        name: f.name, email: f.email, phone: f.phone,
        accommodation_type: f.accommodation_type,
        arrival_date: f.arrival_date, departure_date: f.departure_date,
        guests: parseInt(f.guests_lodging) || 0,
        guests_lodging: isSalle ? null : (parseInt(f.guests_lodging) || null),
        guests_reception: hasSalle && f.guests_reception ? parseInt(f.guests_reception) : null,
        adults: isSalle ? null : (parseInt(f.adults) || null),
        children: parseInt(f.children) || 0,
        event_type: hasSalle ? (f.event_type || null) : null,
        message: f.message,
        amount: parseFloat(f.amount) || 0,
        tourist_tax: parseFloat(f.tourist_tax) || 0,
        deposit_amount: parseFloat(f.deposit_amount) || 0,
        status: f.status, payment_status: f.payment_status, source: f.source,
        force,
      });
      toast.success(`Reservation creee pour ${res.data.reservation?.name || f.name}`);
      setF(EMPTY);
      setConflictConfirm(false);
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      if (e.response?.status === 409) {
        setConflictConfirm(true);
        toast.error(e.response.data?.detail || 'Conflit de disponibilite sur ces dates');
      } else {
        toast.error(e.response?.data?.detail || 'Erreur lors de la creation');
      }
    } finally {
      setSaving(false);
    }
  };

  const L = ({ children }) => <label className="text-xs font-medium text-[#595959] block mb-1">{children}</label>;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-reservation-modal">
        <DialogHeader>
          <DialogTitle>Nouvelle reservation manuelle</DialogTitle>
          <DialogDescription>
            Saisie admin (telephone, sur place...). Aucun email client ni notification n&apos;est envoye.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <L>Formule *</L>
            <Select value={f.accommodation_type} onValueChange={(v) => set('accommodation_type', v)}>
              <SelectTrigger data-testid="nr-accommodation"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(FORMULES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <L>Arrivee *</L>
            <Input type="date" value={f.arrival_date} onChange={(e) => set('arrival_date', e.target.value)} data-testid="nr-arrival" />
          </div>
          <div>
            <L>Depart *</L>
            <Input type="date" value={f.departure_date} onChange={(e) => set('departure_date', e.target.value)} data-testid="nr-departure" />
          </div>

          {availability && (
            <div className="col-span-2" data-testid="nr-availability-banner">
              {availability === 'checking' && (
                <div className="text-xs text-[#888] flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Verification de la disponibilite...</div>
              )}
              {availability === 'available' && (
                <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 flex items-center gap-1.5" data-testid="nr-availability-ok">
                  <CheckCircle2 className="w-4 h-4 shrink-0" strokeWidth={1.5} /> Dates disponibles pour cette formule
                </div>
              )}
              {availability === 'conflict' && (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 flex items-center gap-1.5" data-testid="nr-availability-conflict">
                  <AlertTriangle className="w-4 h-4 shrink-0" strokeWidth={1.5} />
                  <span><strong>Attention :</strong> ces dates chevauchent une reservation ou un blocage existant pour cette formule (risque de double reservation).</span>
                </div>
              )}
            </div>
          )}

          <div>
            <L>Nom du client *</L>
            <Input value={f.name} onChange={(e) => set('name', e.target.value)} placeholder="Jean Dupont" data-testid="nr-name" />
          </div>
          <div>
            <L>Telephone</L>
            <Input value={f.phone} onChange={(e) => set('phone', e.target.value)} placeholder="06..." data-testid="nr-phone" />
          </div>
          <div className="col-span-2">
            <L>Email</L>
            <Input type="email" value={f.email} onChange={(e) => set('email', e.target.value)} placeholder="client@email.fr" data-testid="nr-email" />
          </div>

          {!isSalle && (
            <>
              <div>
                <L>Invites hebergement</L>
                <Input type="number" min="1" value={f.guests_lodging} onChange={(e) => set('guests_lodging', e.target.value)} data-testid="nr-guests-lodging" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <L>Adultes *</L>
                  <Input type="number" min="1" value={f.adults} onChange={(e) => set('adults', e.target.value)} data-testid="nr-adults" />
                </div>
                <div>
                  <L>Enfants</L>
                  <Input type="number" min="0" value={f.children} onChange={(e) => set('children', e.target.value)} data-testid="nr-children" />
                </div>
              </div>
            </>
          )}

          {hasSalle && (
            <>
              <div>
                <L>Invites reception</L>
                <Input type="number" min="0" value={f.guests_reception} onChange={(e) => set('guests_reception', e.target.value)} data-testid="nr-guests-reception" />
              </div>
              <div>
                <L>Type d&apos;evenement</L>
                <Input value={f.event_type} onChange={(e) => set('event_type', e.target.value)} placeholder="Mariage, anniversaire..." data-testid="nr-event-type" />
              </div>
            </>
          )}

          <div className="col-span-2 flex items-end gap-2 flex-wrap bg-[#F7F7F4] rounded-lg p-3">
            <div className="flex-1 min-w-[100px]">
              <L>Montant total (€) *</L>
              <Input type="number" step="0.01" value={f.amount} onChange={(e) => set('amount', e.target.value)} data-testid="nr-amount" />
            </div>
            <div className="flex-1 min-w-[90px]">
              <L>Dont taxe sejour (€)</L>
              <Input type="number" step="0.01" value={f.tourist_tax} onChange={(e) => set('tourist_tax', e.target.value)} data-testid="nr-tourist-tax" />
            </div>
            <div className="flex-1 min-w-[90px]">
              <L>Acompte (€)</L>
              <Input type="number" step="0.01" value={f.deposit_amount} onChange={(e) => set('deposit_amount', e.target.value)} placeholder="30% auto" data-testid="nr-deposit" />
            </div>
            <Button type="button" variant="outline" onClick={calculate} disabled={calculating} className="h-9" data-testid="nr-calculate-btn">
              {calculating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4 mr-1" strokeWidth={1.5} />}
              Calculer
            </Button>
          </div>

          <div>
            <L>Statut</L>
            <Select value={f.status} onValueChange={(v) => set('status', v)}>
              <SelectTrigger data-testid="nr-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="confirmed">Confirmee</SelectItem>
                <SelectItem value="pending">En attente</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <L>Paiement</L>
            <Select value={f.payment_status} onValueChange={(v) => set('payment_status', v)}>
              <SelectTrigger data-testid="nr-payment-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unpaid">Non paye</SelectItem>
                <SelectItem value="deposit_paid">Acompte paye</SelectItem>
                <SelectItem value="paid">Paye integralement</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <L>Source</L>
            <Select value={f.source} onValueChange={(v) => set('source', v)}>
              <SelectTrigger data-testid="nr-source"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <L>Note interne</L>
            <Textarea rows={2} value={f.message} onChange={(e) => set('message', e.target.value)} placeholder="Details, demandes particulieres..." data-testid="nr-message" />
          </div>
        </div>

        {conflictConfirm && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="nr-conflict-confirm">
            <strong>Conflit de disponibilite confirme par le serveur.</strong> Cliquez sur &laquo; Creer quand meme &raquo; uniquement si vous savez ce que vous faites (double reservation assumee).
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="nr-cancel-btn">Annuler</Button>
          {conflictConfirm ? (
            <Button onClick={() => submit(true)} disabled={saving} className="bg-red-600 hover:bg-red-700" data-testid="nr-force-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <AlertTriangle className="w-4 h-4 mr-1" strokeWidth={1.5} />}
              Creer quand meme
            </Button>
          ) : (
            <Button onClick={() => submit(false)} disabled={saving} className="bg-[#2C3E30] hover:bg-[#3a5240]" data-testid="nr-submit-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Creer la reservation
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
